import { Content, List, Root, Trigger } from '@radix-ui/react-tabs';
import {
  type ComponentPropsWithoutRef,
  type ElementRef,
  forwardRef,
  useCallback,
} from 'react';

import { cn } from '@/lib/cn';

type TabsProps<T extends string> = Omit<
  ComponentPropsWithoutRef<typeof Root>,
  'value' | 'onValueChange'
> & {
  value?: T;
  onValueChange?: (value: T) => void;
  validValues?: readonly T[];
};

function Tabs<T extends string>({
  value,
  onValueChange,
  validValues,
  ...props
}: TabsProps<T>) {
  const handleValueChange = useCallback(
    // タブの値が変更された際の処理です。
    // validValues が提供されている場合のみ、渡された値が有効なものかを検証し、
    // 正しい場合にのみ onValueChange を呼び出す。validValues がなければ直接値を渡す。
    (newValue: string) => {
      if (onValueChange) {
        if (validValues) {
          const typedValue = validValues.find((v) => v === newValue);
          if (typedValue) {
            onValueChange(typedValue);
          }
        } else {
          onValueChange(newValue as T);
        }
      }
    },
    [onValueChange, validValues],
  );

  return <Root value={value} onValueChange={handleValueChange} {...props} />;
}

const TabsList = forwardRef<
  ElementRef<typeof List>,
  ComponentPropsWithoutRef<typeof List>
>(({ className, ...props }, ref) => (
  <List
    ref={ref}
    className={cn('flex flex-wrap gap-2', className)}
    {...props}
  />
));
TabsList.displayName = List.displayName;

const TabsTrigger = forwardRef<
  ElementRef<typeof Trigger>,
  ComponentPropsWithoutRef<typeof Trigger>
>(({ className, ...props }, ref) => (
  <Trigger
    ref={ref}
    className={cn(
      'inline-flex items-center justify-center whitespace-nowrap rounded-md px-3 py-1 text-sm font-medium ring-offset-background transition-all',
      'disabled:pointer-events-none disabled:opacity-50',
      'focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
      'data-[state=active]:bg-background data-[state=active]:text-foreground data-[state=active]:shadow-sm',
      className,
    )}
    {...props}
  />
));
TabsTrigger.displayName = Trigger.displayName;

const TabsContent = forwardRef<
  ElementRef<typeof Content>,
  ComponentPropsWithoutRef<typeof Content>
>(({ className, ...props }, ref) => (
  <Content
    ref={ref}
    className={cn(
      'mt-2 ring-offset-background focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
      className,
    )}
    {...props}
  />
));
TabsContent.displayName = Content.displayName;

export { Tabs, TabsList, TabsTrigger, TabsContent };
